// src/lib/api/services/packages.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'
import { logger } from '../../logger'

/**
 * Get all packages
 * Migrated from supabaseApi.js lines 2766-2785
 * @param {string} [providedCompanyId] - Optional company ID (avoids internal lookup)
 */
const _getPackages = async (providedCompanyId = null) => {
  logger.packages('getPackages called')
  
  const companyId = providedCompanyId || await getValidatedCompanyId()
  if (!companyId) {
    logger.packages('No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('packages')
    .select(`
      *,
      package_services(
        id,
        quantity,
        services(
          id,
          name,
          description,
          price,
          price_type,
          service_categories(name)
        )
      ),
      package_categories(
        category_id,
        service_categories(
          id,
          name
        )
      )
    `)
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    
  if (error) {
    logger.error('[packages] Error fetching packages:', error)
    throw error
  }

  // Transform the data to include categories array for backward compatibility
  if (data) {
    data.forEach(pkg => {
      pkg.categories = pkg.package_categories?.map(pc => pc.service_categories).filter(Boolean) || []
      pkg.category_ids = pkg.categories.map(c => c.id)
      // Keep backward compatibility with single category_id
      if (pkg.category_ids.length > 0 && !pkg.category_id) {
        pkg.category_id = pkg.category_ids[0]
      }
    })
  }

  logger.packages('getPackages success, found', data?.length || 0, 'packages')
  return createSuccessResponse(data || [])
}

/**
 * Create a new package
 * Migrated from supabaseApi.js lines 2786-2850
 */
const _createPackage = async (packageData) => {
  console.log('[packages] createPackage called with data:', packageData)
  
  validateRequired(packageData, 'Package data')
  validateRequired(packageData.name, 'Package name')
  validateRequired(packageData.price, 'Package price')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found. Please ensure you are logged in and associated with a company.')
  }
  
  const { services, category_ids, ...packageInfo } = packageData
  
  // Remove category_id from packageInfo if it exists (we use category_ids now)
  delete packageInfo.category_id
  
  // Validate required fields
  if (!packageInfo.name || packageInfo.name.trim() === '') {
    throw new Error('Package name is required')
  }
  
  if (!packageInfo.price || packageInfo.price <= 0) {
    throw new Error('Package price must be greater than 0')
  }
  
  // Get the current user ID for created_by field
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('User not authenticated')
  }

  // Prepare the package data for insertion
  const packageInsertData = {
    name: sanitizeInput(packageInfo.name),
    description: packageInfo.description ? sanitizeInput(packageInfo.description) : null,
    price: parseFloat(packageInfo.price),
    is_active: packageInfo.is_active !== undefined ? packageInfo.is_active : true,
    company_id: companyId,
    created_by: userId
  }
  
  console.log('[packages] Creating package with data:', packageInsertData)
  
  // Create the package
  const { data: packageResult, error: packageError } = await supabase
    .from('packages')
    .insert([packageInsertData])
    .select()
    .single()
    
  if (packageError) {
    console.error('[packages] Package creation error:', packageError)
    throw packageError
  }
  
  // Create package categories relationships
  if (category_ids && category_ids.length > 0) {
    const packageCategories = category_ids.map(categoryId => ({
      package_id: packageResult.id,
      category_id: categoryId
    }))
    
    const { error: categoriesError } = await supabase
      .from('package_categories')
      .insert(packageCategories)
      
    if (categoriesError) {
      console.error('[packages] Package categories creation error:', categoriesError)
      throw categoriesError
    }
  }
  
  // Create package services relationships
  if (services && services.length > 0) {
    const packageServices = services.map(service => ({
      package_id: packageResult.id,
      service_id: service.service_id,
      quantity: service.quantity || 1
    }))
    
    const { error: servicesError } = await supabase
      .from('package_services')
      .insert(packageServices)
      
    if (servicesError) {
      console.error('[packages] Package services creation error:', servicesError)
      throw servicesError
    }
  }
  
  console.log('[packages] createPackage success, created package:', packageResult.id)
  return packageResult
}

/**
 * Update an existing package
 * Migrated from supabaseApi.js lines 2851-2890
 */
const _updatePackage = async (packageId, packageData) => {
  console.log('[packages] updatePackage called for ID:', packageId, 'with data:', packageData)
  
  validateRequired(packageId, 'Package ID')
  validateRequired(packageData, 'Package data')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { services, category_ids, ...packageInfo } = packageData
  
  // Remove category_id from packageInfo if it exists (we use category_ids now)
  delete packageInfo.category_id
  
  // Sanitize string fields
  if (packageInfo.name) {
    packageInfo.name = sanitizeInput(packageInfo.name)
  }
  if (packageInfo.description) {
    packageInfo.description = sanitizeInput(packageInfo.description)
  }
  
  // Update the package
  const { data: packageResult, error: packageError } = await supabase
    .from('packages')
    .update({ ...packageInfo, company_id: companyId })
    .eq('id', packageId)
    .select()
    .single()
    
  if (packageError) {
    console.error('[packages] Package update error:', packageError)
    throw packageError
  }
  
  // Update package categories relationships
  if (category_ids !== undefined) {
    // Delete existing package categories
    const { error: deleteError } = await supabase
      .from('package_categories')
      .delete()
      .eq('package_id', packageId)
      
    if (deleteError) {
      console.error('[packages] Error deleting package categories:', deleteError)
      throw deleteError
    }
    
    // Insert new package categories
    if (category_ids.length > 0) {
      const packageCategories = category_ids.map(categoryId => ({
        package_id: packageId,
        category_id: categoryId
      }))
      
      const { error: categoriesError } = await supabase
        .from('package_categories')
        .insert(packageCategories)
        
      if (categoriesError) {
        console.error('[packages] Error inserting package categories:', categoriesError)
        throw categoriesError
      }
    }
  }
  
  // Update package services relationships
  if (services) {
    // Delete existing package services
    const { error: deleteError } = await supabase
      .from('package_services')
      .delete()
      .eq('package_id', packageId)
      
    if (deleteError) {
      console.error('[packages] Error deleting package services:', deleteError)
      throw deleteError
    }
    
    // Insert new package services
    if (services.length > 0) {
      const packageServices = services.map(service => ({
        package_id: packageId,
        service_id: service.service_id,
        quantity: service.quantity || 1
      }))
      
      const { error: servicesError } = await supabase
        .from('package_services')
        .insert(packageServices)
        
      if (servicesError) {
        console.error('[packages] Error inserting package services:', servicesError)
        throw servicesError
      }
    }
  }
  
  console.log('[packages] updatePackage success for package:', packageResult.id)
  return packageResult
}

/**
 * Delete a package
 * Migrated from supabaseApi.js lines 2891-2910
 */
const _deletePackage = async (packageId) => {
  console.log('[packages] deletePackage called for ID:', packageId)
  
  validateRequired(packageId, 'Package ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Delete package services first
  const { error: servicesError } = await supabase
    .from('package_services')
    .delete()
    .eq('package_id', packageId)
    
  if (servicesError) {
    console.error('[packages] Error deleting package services:', servicesError)
    throw servicesError
  }
  
  // Delete the package
  const { error } = await supabase
    .from('packages')
    .delete()
    .eq('id', packageId)
    
  if (error) {
    console.error('[packages] Error deleting package:', error)
    throw error
  }
  
  console.log('[packages] deletePackage success for package:', packageId)
  return { success: true }
}

/**
 * Duplicate a package
 * Migrated from supabaseApi.js lines 2911-2950
 */
const _duplicatePackage = async (packageId, newName = null) => {
  console.log('[packages] duplicatePackage called for ID:', packageId, 'with new name:', newName)
  
  validateRequired(packageId, 'Package ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Get the original package with its services and categories
  const { data: originalPackage, error: fetchError } = await supabase
    .from('packages')
    .select(`
      *,
      package_services(
        service_id,
        quantity
      ),
      package_categories(
        category_id
      )
    `)
    .eq('id', packageId)
    .eq('company_id', companyId)
    .single()
    
  if (fetchError) {
    console.error('[packages] Error fetching original package:', fetchError)
    throw fetchError
  }
  
  // Create new package data
  const newPackageData = {
    name: newName || `${originalPackage.name} (Copy)`,
    description: originalPackage.description,
    category_ids: originalPackage.package_categories?.map(pc => pc.category_id) || [],
    price: originalPackage.price,
    is_active: originalPackage.is_active,
    services: originalPackage.package_services.map(ps => ({
      service_id: ps.service_id,
      quantity: ps.quantity
    }))
  }
  
  console.log('[packages] Duplicating package with data:', newPackageData)
  const result = await _createPackage(newPackageData)
  
  console.log('[packages] duplicatePackage success, created new package:', result.id)
  return result
}

// Export all functions with error handling
export const getPackages = withErrorHandling(_getPackages, 'get packages', 'packages')
export const createPackage = withErrorHandling(_createPackage, 'create package', 'package')
export const updatePackage = withErrorHandling(_updatePackage, 'update package', 'package')
export const deletePackage = withErrorHandling(_deletePackage, 'delete package', 'package')
export const duplicatePackage = withErrorHandling(_duplicatePackage, 'duplicate package', 'package')